import { useEffect, useRef } from 'react';

import { cn } from '../../shared/lib/cn';
import { CodeView } from '../../shared/ui/code-view';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Modal } from '../../shared/ui/modal';
import { Select } from '../../shared/ui/select';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  isDraft,
  type IPin,
  type TTransform,
  type TTransformVersionSummary,
} from '../../entities/transform';
import { INLINE_SAMPLE, useDryRun } from './use-dry-run';
import { RunResult } from './run-result';

interface IProps {
  transform: TTransform;
  versions: TTransformVersionSummary[];
  pins: IPin[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TransformDryRun = ({ transform, versions, pins, open, onOpenChange }: IProps) => {
  const state = useDryRun(transform, open);
  const verdict = useRef<HTMLDivElement>(null);
  const landed = state.run.isSuccess ? state.run.submittedAt : 0;

  useEffect(() => {
    if (landed) verdict.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [landed]);

  const sampleOptions = [
    { value: INLINE_SAMPLE, label: 'Paste a body' },
    ...state.bookmarks.map((bookmark) => ({ value: bookmark.id, label: bookmark.name })),
  ];

  const versionOptions = versions.map((version) => ({
    value: String(version.version),
    label: isDraft(version.version, transform.latestVersion, pins)
      ? `v${version.version} · draft`
      : `v${version.version}`,
    description: version.comment ?? undefined,
  }));

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Try it on a sample"
      width="lg"
      description="Nothing is delivered and no run is started. This is the code on a body, and that is all"
      submit={{
        label: 'Run it',
        onSubmit: () => state.run.mutate(),
        disabled: !state.canRun,
        waiting: state.run.isPending,
        waitingLabel: 'Running',
      }}
    >
      <div className="max-h-body overflow-auto">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>Sample</FieldLabel>
            <Select
              value={state.sample}
              onChange={state.setSample}
              options={sampleOptions}
              aria-label="Sample"
            />
            <Hint>A bookmark, or paste a body</Hint>
          </div>
          <div>
            <FieldLabel>Version</FieldLabel>
            <Select
              value={String(state.version)}
              onChange={(next) => state.setVersion(Number(next))}
              options={versionOptions}
              aria-label="Version"
            />
            <Hint>Any version, not just the draft</Hint>
          </div>
        </div>

        <div className="mt-4">
          <FieldLabel htmlFor="dry-run-body">What goes in</FieldLabel>
          {state.sample === INLINE_SAMPLE ? (
            <CodeView
              id="dry-run-body"
              invalid={state.inlineError !== null}
              value={state.body}
              onChange={state.setBody}
              className={cn(
                'min-h-json-field max-h-facets rounded-panel border',
                'focus-within:border-border-focus focus-within:ring-3 focus-within:ring-focus',
                state.inlineError !== null ? 'border-danger' : 'border-border-raised',
              )}
            />
          ) : state.incomingPending ? (
            <LoadingState rows={4} />
          ) : (
            <CodeView value={state.incoming ?? ''} className="max-h-facets rounded-panel" />
          )}
          {state.inlineError && (
            <p className="mt-label text-meta text-danger">Not valid JSON — {state.inlineError}</p>
          )}
        </div>

        {state.source.isError && <ErrorState error={state.source.error} />}
        {state.run.isError && <ErrorState error={state.run.error} />}

        {state.run.data && (
          <div ref={verdict} className="mt-section border-t border-border-sunken pt-5">
            <RunResult run={state.run.data} />
          </div>
        )}
      </div>
    </Modal>
  );
};
